<script lang="ts">
  import { onMount } from "svelte";
  import type { Picker } from "./types";
  import type { DateTime } from "./lib/date-time";
  import { parse, format, type InputValueStore, inputValueChanges } from "./lib/utils";
  import { SelectionRange } from "./lib/range";
  import { DatePickerTranslations } from "./DatePicker.i18n";
  import DatePicker from "./DatePicker.svelte";
  import Overlay from "./overlay/Overlay.svelte";

  let pickerOpen = false;

  /**
   * TODO fix - removing (clearing) date after picking!
   */

  export let host: HTMLInputElement;
  export let language: "de" | "it" | "en";
  export let minDate: DateTime;
  export let overlay: "inline" | "modal";
  export let picker: Picker = "year";

  $: T = DatePickerTranslations(language);

  const selection = new SelectionRange(minDate, 0, true);
  let currentValue: string | undefined;

  /**
   * public API
   */
  export function setValue(value: string | undefined) {
    currentValue = value || undefined;
    const date = typeof value === "string" ? value : null;
    selection.set(parse(date), parse(date));
    selection.update();
  }

  /**
   * public API
   */
  export function getValue() {
    return currentValue;
  }

  /**
   * public API
   */
  export function open() {
    pickerOpen = true;
  }

  /**
   * public API
   */
  export function close() {
    pickerOpen = false;
  }

  let initialized: boolean;
  let inputValue: InputValueStore;
  onMount(() => {
    host.autocomplete = "off";
    host.addEventListener("click", () => (pickerOpen = true));

    inputValue = inputValueChanges(host, language, "host");

    inputValue.subscribe((value) => {
      if (inputValue.initialized) {
        selection.set(value, value);
      }
    });

    initialized = true;
  });

  $: selectionValue = selection.value;
  $: {
    if (initialized) {
      const [from, to] = $selectionValue;

      if (inputValue.date?.ticks !== from?.ticks) {
        inputValue.set(from, true);
        currentValue = inputValue.date?.toISOString() || undefined;
        host.dispatchEvent(new Event("input"));
        host.dispatchEvent(new Event("change"));
        host.dispatchEvent(new Event("blur"));
      }

      pickerOpen = false;
    }
  }

  function reset() {
    let value = [null, null];
    selection.value.subscribe((_) => (value = _))();
    selection.set(value[0], value[1]);
  }
</script>

<Overlay {overlay} closeLabel={T.get("overlay.close")} input={host} bind:open={pickerOpen} on:close={(_) => setTimeout(reset, 200)} let:overlay>
  <DatePicker bind:picker {selection} {language} on:resize={(_) => overlay.resize()} />
</Overlay>

<style lang="scss" global>
</style>
